import {computed, type ComputedRef, type Ref, ref, shallowRef, type ShallowRef} from 'vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import type {Questionnaire} from '@/ts/types/dto/interventions-dto';
import type {QuestionnaireAnswers, SessionRequestQuestionnaire} from '@/ts/types/dto/session-request-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

interface TodayQuestionnaireData {
  /**
   * Whether an answer went through the overlay that is closing. The page reloads on that, not on
   * the overlay closing: a questionnaire opened and dismissed changes nothing, so refetching would
   * put the page behind a spinner to redraw exactly what was there.
   *
   * Read it through `settle()` rather than directly. It turns true when the backend accepts the
   * answers, and the modal asks to be closed in the same tick it asks to submit, so read on its own
   * it is still false at the only moment the page looks at it.
   */
  answered: Ref<boolean>;
  /**
   * Whether the questionnaire on screen is one the user may only read back, which the modal draws
   * as its results instead of its stepper (IPD-2772: a completed questionnaire must not be
   * modified). Decided by whoever opens it and held for as long as it stays open, because what the
   * questionnaire itself reports changes under the modal: `isAnswered` is true of every
   * questionnaire the user has ever answered, reassessments included, and it turns true again on
   * the read-back that follows a submission.
   */
  readonly: Ref<boolean>;
  /** The questionnaire on screen, or null while none is. Drives the modal's own `v-if`. */
  questionnaire: ShallowRef<Questionnaire | null>;
  /** The answers the modal collects, which it owns and this only carries. */
  answers: Ref<SessionRequestQuestionnaire[] | null>;
  /** True while opening or submitting, which the modal draws as its own busy state. */
  busy: ComputedRef<boolean>;
  /** [readonly] says the user is looking their answers up rather than giving them. */
  open(id: UuidDTO, readonly?: boolean): Promise<void>;
  submit(): Promise<void>;
  /**
   * Waits for an answer still in flight and reports whether one went through, which is what the
   * page decides its reload on.
   *
   * It exists because of how the modal ends a questionnaire with no score: it emits its submit and
   * its close synchronously, one after the other, so the close is handled while the POST has not
   * left yet. A score-less questionnaire is the common case here, so reading `answered` at that
   * moment usually said no and the page never refreshed itself.
   */
  settle(): Promise<boolean>;
  close(): void;
}

/**
 * The questionnaire overlay the Today page opens, for the tiles that ask for one: the Upcoming
 * tile's onboarding CTA, the assessment slider's open card, and an assessment row of the task list.
 * All three carry an id and no route, because a questionnaire is a modal in this app rather than a
 * page, and the page is what owns opening it.
 *
 * The id is all the tiles send, so the questionnaire itself is fetched here. The payload does carry
 * one — the same `ComponentQuestionnaire` this endpoint answers with — but it arrives with its
 * `questions` array empty, which is a questionnaire the modal has nothing to draw. Fetching it also
 * means the answers and the score come from the same read the rest of the app uses, rather than
 * from a copy that was made when the page loaded.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: reload the page after an answer. Answering moves the user
 * between states of the Today page — the onboarding one in particular empties the Upcoming tile's
 * welcome face and fills the assessment tile — so the page is stale afterwards, and refreshing it
 * is the caller's call to make once the overlay is out of the way. Doing it from here would blank
 * the bento behind the modal the user is still reading their score in.
 */
export function useTodayQuestionnaire(): TodayQuestionnaireData {
  const api = useApi();
  const questionnaire = shallowRef<Questionnaire | null>(null);
  const answers = ref<SessionRequestQuestionnaire[] | null>(null);
  const answered = ref(false);
  const readonly = ref(false);

  /**
   * The submission in flight, so `settle()` can wait for it. Not reactive: nothing renders from it,
   * and `busy` already reports the same thing to the modal.
   */
  let pending: Promise<void> | null = null;

  const [fetchQuestionnaire, opening] = useLoadingNotification(async(id: UuidDTO, readonlyOnOpen: boolean) => {
    // Cleared before the request rather than after it: an overlay that opened with the previous
    // questionnaire's answers still in hand would submit them against this one.
    answers.value = null;
    answered.value = false;
    readonly.value = readonlyOnOpen;
    // And the submission they belong to, so `settle()` cannot wait on another questionnaire's.
    pending = null;
    questionnaire.value = await api.me.getQuestionnaire(id);
  });

  const [postAnswers, submitting] = useLoadingNotification(async() => {
    const collected = answers.value;
    const current = questionnaire.value;
    if (!collected || !current) {
      return;
    }
    // The modal collects one entry per question and wraps each in the session-request shape the
    // answer endpoint takes; only the inner answers travel.
    const payload = collected
      .map((entry) => (entry as unknown as {questionnaireAnswers?: QuestionnaireAnswers}).questionnaireAnswers)
      .filter((entry): entry is QuestionnaireAnswers => Boolean(entry));
    await api.me.postQuestionnaire(payload);
    answered.value = true;

    // Read back, because the score is computed on submit and the modal shows it on its last step:
    // it waits for the questionnaire it was handed to come back carrying one. A questionnaire with
    // no score simply comes back without one and the modal closes on the last question.
    const scored = await api.me.getQuestionnaire(current.id);
    // Only if the overlay is still showing the one that was submitted: closing it mid-request, or
    // opening another from a tile behind it, must not be undone by an answer that arrives late.
    if (questionnaire.value?.id === current.id) {
      questionnaire.value = scored;
    }
  });

  return {
    questionnaire,
    answers,
    answered,
    readonly,
    busy: computed(() => opening.value || submitting.value),
    async open(id: UuidDTO, readonlyOnOpen = false): Promise<void> {
      await fetchQuestionnaire(id, readonlyOnOpen);
    },
    async submit(): Promise<void> {
      // The three ways the modal asks to send answers that must not go out: a questionnaire opened
      // to be read back, one whose answers went through on an earlier pass through this same
      // overlay, and one whose submission is still travelling. It offers its last step again after
      // a step back and reports finishing more than once, so none of these is theoretical.
      //
      // `submitting` is here for `pending` rather than for the request: `postAnswers` already
      // returns at once while it runs, but the second call would replace `pending` with a promise
      // that resolves immediately and `settle()` would then report no answer for one that landed.
      //
      // Here rather than inside the request: the flag is set once the answers land, so reading it
      // in there is the read-then-write the linter refuses on principle, and this is the only
      // caller anyway.
      if (readonly.value || answered.value || submitting.value) {
        return;
      }
      pending = postAnswers();
      await pending;
    },
    async settle(): Promise<boolean> {
      // Awaiting the same promise from two callers is safe, and there are two: the modal reports
      // finishing through both its own event and the dialog closing.
      await pending;

      return answered.value;
    },
    close(): void {
      // `answered` goes back to false here as well as on open, so a second pass through the same
      // overlay cannot inherit the first one's answer. Closing twice, which the modal does, then
      // reloads once.
      answered.value = false;
      readonly.value = false;
      pending = null;
      // The answers are deliberately NOT cleared. `open()` clears them before every fetch, so
      // nothing stale can reach the next questionnaire, and leaving them here keeps a submission
      // that failed available to whatever offers the user another go.
      questionnaire.value = null;
    },
  };
}
